import { FormPlanoAcaoRAM } from "@/contexts/fiscalizacao/relatorioAM/components/FormPlanoAcaoRAM/FormPlanoAcaoRAM";
import { FormPreviewRAM } from "@/contexts/fiscalizacao/relatorioAM/components/FormPreviewRAM/FormPreviewRAM";
import { ModalPlanoAcaoRelatorioAM } from "@/contexts/fiscalizacao/relatorioAM/components/ModalHistoricoPlanoAcaoRAM/ModalPlanoAcaoRelatorioAM";
import { useRelatorioAM } from "@/contexts/fiscalizacao/relatorioAM/hooks/useRelatorioAM";
import { PageLoader } from "@/shared/components/PageLoader/PageLoader";
import { useNetworkStatus } from "@/shared/hooks/useNetworkStatus";
import { ArrowLeftOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Col,
  Collapse,
  Divider,
  Grid,
  Result,
  Row,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

const ID_ALTERNATIVA_NAO_CONFORME = 2;

export function NovoPlanoAcaoRelatorioAM() {
  const screens = Grid.useBreakpoint();
  const { relatorioId } = useParams<{ relatorioId: string }>();
  const { isOnline } = useNetworkStatus();
  const navigate = useNavigate();
  const { getRelatorioAMByIdApi, canMakePlanoAcao } = useRelatorioAM();
  const [isOpenModalHistorico, setIsOpenModalHistorico] = useState(false);
  const [itemIdSelecionado, setItemIdSelecionado] = useState<number | null>(
    null
  );

  const {
    data: relatorio,
    isLoading: loadingRelatorio,
    refetch,
  } = useQuery({
    queryKey: ["RELATORIO_AM_BY_ID", relatorioId],
    enabled: isOnline && !!relatorioId,
    queryFn: async () => {
      const response = await getRelatorioAMByIdApi(Number(relatorioId));
      return response;
    },
  });

  const itensNaoConformes = useMemo(() => {
    if (!relatorio?.itens) return [];
    return relatorio.itens.filter(
      (item) =>
        item.alternativa?.id === ID_ALTERNATIVA_NAO_CONFORME &&
        item.status === "AGUARDANDO_PLANO_DE_ACAO"
    );
  }, [relatorio]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleOpenHistoricoPlanoAcao = (itemId: number) => {
    setItemIdSelecionado(itemId);
    setIsOpenModalHistorico(true);
  };

  const handleCloseHistoricoPlanoAcao = () => {
    setIsOpenModalHistorico(false);
  };

  useEffect(() => {
    document.title =
      "Inspeção Mobile - Novo Plano de Ação Relatórios de Auditoria e Monitoria";
  }, []);

  if (!isOnline) {
    return <Result title="Sem conexão com a internet" status={"error"} />;
  }

  if (loadingRelatorio) {
    return <PageLoader />;
  }

  if (!relatorio) {
    return <Result title="Relatório não encontrado" status={"error"} />;
  }

  const userCanMakePlanoAcao = canMakePlanoAcao(
    relatorio?.formulario?.classificacaoFormulario,
    relatorio?.estabelecimento?.id
  );

  // if (!userCanMakePlanoAcao) {
  //   return <Navigate to="/" />;
  // }

  return (
    <>
      <Typography.Title
        level={screens.xs ? 4 : 3}
        style={{ textAlign: "center" }}
      >
        Plano de Ação Relatório de Auditoria e Monitoria {relatorio?.id}
      </Typography.Title>

      <Divider />

      <FormPreviewRAM relatorio={relatorio} />

      <Divider orientation="left">
        <Typography.Title level={5}>Planos de Ação</Typography.Title>
      </Divider>

      {itensNaoConformes.length === 0 && (
        <Row justify="center">
          <Col>
            <Typography.Text>
              Não há itens não conformes aguardando plano de ação.
            </Typography.Text>
          </Col>
        </Row>
      )}

      {itensNaoConformes.length > 0 && (
        <Collapse
          accordion
          expandIconPosition="end"
          items={itensNaoConformes.map((item) => ({
            key: item.uuid,
            label: item.pergunta?.nome,
            children: (
              <Row gutter={[8, 8]}>
                <Col xs={24} sm={12}>
                  <Typography.Text type="secondary">
                    <strong>Seção:</strong> {item.secao?.nome}
                  </Typography.Text>
                </Col>
                <Col xs={24} sm={12}>
                  <Typography.Text type="secondary">
                    <strong>Data Resposta:</strong>{" "}
                    {dayjs(item.dataResposta).format("DD/MM/YYYY")}
                  </Typography.Text>
                </Col>
                <Col xs={24} sm={12}>
                  <Typography.Text type="danger">
                    <strong>Alternativa: </strong>
                    {item.alternativa?.nome}
                  </Typography.Text>
                </Col>
                <Col xs={24} sm={12}>
                  <Typography.Text type="secondary">
                    <strong>Observação:</strong> {item.resposta || "-"}
                  </Typography.Text>
                </Col>
                <Col>
                  <Button
                    type="primary"
                    icon={<UnorderedListOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenHistoricoPlanoAcao(item.id!);
                    }}
                  >
                    Histórico
                  </Button>
                </Col>
                <Col xs={24}>
                  <FormPlanoAcaoRAM
                    item={item}
                    relatorio={relatorio}
                    refetchRelatorio={refetch}
                  />
                </Col>
              </Row>
            ),
          }))}
        />
      )}

      <Divider />

      <Row justify="center" gutter={[10, 10]}>
        <Col>
          <Button
            htmlType="button"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            style={{ width: 200 }}
          >
            Voltar
          </Button>
        </Col>
      </Row>

      <ModalPlanoAcaoRelatorioAM
        open={isOpenModalHistorico}
        onClose={handleCloseHistoricoPlanoAcao}
        ramItemId={itemIdSelecionado!}
      />
    </>
  );
}
